/**
 * Picks the panel for `?task=<id>` on /welcome.
 *
 * Task panels are a step *inside* the setup flow, not a detour into the
 * dashboard: the client came here to be told what to do next, and sending them
 * off to a settings screen mid-flow is how people fall out of it.
 */

import React from 'react';
import { Panel, PanelBody } from '../ui/kit';
import { TaskShell } from './TaskShell';
import ConfirmDetailsTask from './tasks/ConfirmDetailsTask';
import PhotosTask from './tasks/PhotosTask';
import ReviewDraftTask from './tasks/ReviewDraftTask';
import DomainTask from './tasks/DomainTask';
import type { SetupStatus } from '../../lib/setupFlow';
import type { TaskRecord } from '../../lib/setupTasks';

export default function TaskRouter({
  taskId,
  status,
  record,
  editorHref,
  onBack,
  onComplete,
}: {
  taskId: string;
  status: SetupStatus;
  record?: TaskRecord;
  editorHref: string;
  onBack: () => void;
  onComplete: (data: Record<string, string>) => void;
}) {
  const shared = { status, record, onBack, onComplete };

  switch (taskId) {
    case 'confirm-details':
      return <ConfirmDetailsTask {...shared} />;
    case 'photos':
      return <PhotosTask {...shared} editorHref={editorHref} />;
    case 'review-draft':
      return <ReviewDraftTask {...shared} />;
    case 'domain':
      return <DomainTask {...shared} />;
    default:
      return (
        <TaskShell eyebrow="Setup" title="We handle this one." onBack={onBack}>
          <Panel>
            <PanelBody>
              <p className="text-sm leading-relaxed text-muted">
                Nothing needed from you here — it&apos;s on our list and you&apos;ll see it tick over
                on the tracker.
              </p>
            </PanelBody>
          </Panel>
        </TaskShell>
      );
  }
}
